import { FunctionComponent, ReactElement } from "react";

import {
    FormControl,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    TextField,
    Tooltip,
} from "@mui/material";

import { InfoRounded } from "@mui/icons-material";

import { FormikErrors, FormikTouched } from "formik";

import { Paper } from "../../../components/common";
import {
    FreebieRuleStatus,
    FreebieRuleType,
    FreebieRuleTypeNames,
    freebieRuleStatusNames,
} from "../../../utils/enums";

import {
    FreebieRuleSection,
    IFreebieRuleForm,
    RadioErrorMessage,
    Row,
    SectionTitle,
    SelectField,
    StyledArrowDown,
    TextFieldContainer,
    disableScrollWheelInput,
} from "./common";

export interface ITypeSectionProps {
    values: IFreebieRuleForm;
    touched: FormikTouched<IFreebieRuleForm>;
    errors: FormikErrors<IFreebieRuleForm>;
    onChange: {
        (e: React.ChangeEvent<any>): void;
        <T_1 = string | React.ChangeEvent<any>>(
            field: T_1,
        ): T_1 extends React.ChangeEvent<any>
            ? void
            : (e: string | React.ChangeEvent<any>) => void;
    };
    onBlur: {
        (e: React.FocusEvent<any, Element>): void;
        <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
    };
}

const freebieRuleTypeList = Object.values(FreebieRuleType).filter(
    (value) => typeof value !== "string",
);
const freebieRuleStatusList = Object.values(FreebieRuleStatus).filter(
    (value) => typeof value !== "string",
);

export const TypeSection: FunctionComponent<ITypeSectionProps> = (
    props: ITypeSectionProps,
): ReactElement => {
    const { values, errors, touched, onChange, onBlur } = props;
    return (
        <Paper>
            <FreebieRuleSection>
                <SectionTitle>General</SectionTitle>
                <Row>
                    <TextFieldContainer>
                        <TextField
                            fullWidth={true}
                            required={true}
                            name="name"
                            label="Name"
                            value={values.name}
                            onChange={onChange}
                            size="small"
                            variant="outlined"
                            onBlur={onBlur}
                            error={Boolean(errors.name) && touched.name}
                            helperText={touched.name && errors.name}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer>
                        <FormControl
                            size="small"
                            fullWidth={true}
                            required={true}
                        >
                            <InputLabel>Status</InputLabel>
                            <SelectField
                                name="status"
                                label="Status"
                                fullWidth={true}
                                size="small"
                                value={values.status}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={Boolean(errors.status) && touched.status}
                                IconComponent={(props) => (
                                    <StyledArrowDown {...props} />
                                )}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <Tooltip
                                            enterTouchDelay={0}
                                            placement="top"
                                            title="Status of the rule. Only active rules are evaluated on cart mutations."
                                        >
                                            <InfoRounded
                                                htmlColor="#626262"
                                                fontSize="small"
                                            />
                                        </Tooltip>
                                    </InputAdornment>
                                }
                            >
                                {freebieRuleStatusList.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        <ListItemText
                                            primary={
                                                freebieRuleStatusNames[
                                                    Number(status)
                                                ]
                                            }
                                        />
                                    </MenuItem>
                                ))}
                            </SelectField>
                            {Boolean(errors.status) && touched.status && (
                                <RadioErrorMessage>
                                    {errors.status}
                                </RadioErrorMessage>
                            )}
                        </FormControl>
                    </TextFieldContainer>
                </Row>
                <Row>
                    <TextFieldContainer>
                        <FormControl
                            size="small"
                            fullWidth={true}
                            required={true}
                        >
                            <InputLabel>Type</InputLabel>
                            <SelectField
                                name="type"
                                label="Type"
                                fullWidth={true}
                                size="small"
                                value={values.type}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={Boolean(errors.type) && touched.type}
                                IconComponent={(props) => (
                                    <StyledArrowDown {...props} />
                                )}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <Tooltip
                                            enterTouchDelay={0}
                                            placement="top"
                                            title={
                                                <>
                                                    Variant eligibility:
                                                    freebie is granted when a
                                                    trigger product is in the
                                                    cart.
                                                    <br />
                                                    <br />
                                                    Minimum cart value: freebie
                                                    is granted once the cart
                                                    crosses a minimum value.
                                                    Only the highest threshold
                                                    the cart has crossed
                                                    applies.
                                                    <br />
                                                    <br />
                                                    Discount code: freebie is
                                                    granted when a specific
                                                    discount code is applied.
                                                </>
                                            }
                                        >
                                            <InfoRounded
                                                htmlColor="#626262"
                                                fontSize="small"
                                            />
                                        </Tooltip>
                                    </InputAdornment>
                                }
                            >
                                {freebieRuleTypeList.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        <ListItemText
                                            primary={
                                                FreebieRuleTypeNames[
                                                    type as FreebieRuleType
                                                ]
                                            }
                                        />
                                    </MenuItem>
                                ))}
                            </SelectField>
                            {Boolean(errors.type) && touched.type && (
                                <RadioErrorMessage>
                                    {errors.type}
                                </RadioErrorMessage>
                            )}
                        </FormControl>
                    </TextFieldContainer>
                    <TextFieldContainer>
                        <TextField
                            type="number"
                            fullWidth={true}
                            required={true}
                            name="quantity"
                            label="Quantity"
                            value={values.quantity}
                            onChange={onChange}
                            size="small"
                            variant="outlined"
                            onBlur={onBlur}
                            error={Boolean(errors.quantity) && touched.quantity}
                            helperText={touched.quantity && errors.quantity}
                        />
                    </TextFieldContainer>
                </Row>
                <Row>
                    <TextFieldContainer>
                        <TextField
                            type="number"
                            fullWidth={true}
                            required={true}
                            name="priority"
                            label="Priority"
                            value={values.priority}
                            onChange={onChange}
                            size="small"
                            variant="outlined"
                            onBlur={onBlur}
                            onWheel={disableScrollWheelInput}
                            error={Boolean(errors.priority) && touched.priority}
                            helperText={touched.priority && errors.priority}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip
                                            enterTouchDelay={0}
                                            placement="top"
                                            title="Decides which rule wins when more than one grants the same freebie. Higher wins."
                                        >
                                            <InfoRounded
                                                htmlColor="#626262"
                                                fontSize="small"
                                            />
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </TextFieldContainer>
                    <TextFieldContainer />
                </Row>
            </FreebieRuleSection>
        </Paper>
    );
};
